import React, { useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AiOutlinePlus, AiOutlineDelete } from "react-icons/ai";
import Tooltip from "@mui/material/Tooltip";

// Validation schema for each category
const categorySchema = yup.object().shape({
  category: yup.string().required("Category is required"),
  suggested_category: yup.string().required("Suggested category is required"),
});

// Form schema for array of categories
const schema = yup.object().shape({
  categories: yup
    .array()
    .of(categorySchema)
    .min(1, "At least one category is required"),
});

function CategoryForm({ onSubmit, btnName, updateData, btnHide }) {

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      categories: [{ category: "", suggested_category: "" }],
    },
  });

  // Prefill form when updateData changes
  useEffect(() => {
    if (updateData) {
      reset({ categories: [{ ...updateData, category: updateData?.category, suggested_category: updateData?.suggested_category }] });
    }
  }, [updateData, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "categories",
  });


  return (
    <div className="max-w-lg mx-auto p-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="p-2 pb-3 border-b rounded-lg space-y-2 relative"
          >
            <div className="font-semibold text-center">Category {index + 1}</div>

            {/* Category Input */}
            <Controller
              name={`categories.${index}.category`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="Enter category"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            />
            {errors.categories?.[index]?.category && (
              <p className="text-red-500 text-sm mt-1">
                {errors.categories[index].category.message}
              </p>
            )}

            {/* Suggested Category Input */}
            <Controller
              name={`categories.${index}.suggested_category`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="Enter suggested category"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            />
            {errors.categories?.[index]?.suggested_category && (
              <p className="text-red-500 text-sm mt-1">
                {errors.categories[index].suggested_category.message}
              </p>
            )}

            {/* Remove button with MUI Tooltip */}
            {fields.length > 1 && (
              <Tooltip title="Remove Category" placement="top">
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="absolute top-0 right-2 text-red-500 hover:text-red-700"
                >
                  <AiOutlineDelete size={20} />
                </button>
              </Tooltip>
            )}
          </div>
        ))}

        {/* Add More + Submit Buttons */}
        <div className="flex gap-3">
          {
            !btnHide &&
            <Tooltip title="Add More Category" placement="top">
              <button
                type="button"
                onClick={() => append({ category: "", suggested_category: "" })}
                className="flex items-center justify-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
              >
                <AiOutlinePlus size={20} />
              </button>
            </Tooltip>

          }
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
          >
            {btnName}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CategoryForm;
